import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { ArrowLeft, Target, Trophy, Clock, TrendingUp, Loader } from "lucide-react";

export default function Progress() {
  const navigate = useNavigate();
  const [progress, setProgress] = useState([]);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [progressRes, examsRes] = await Promise.all([
        api.get("/progress"),
        api.get("/mock-exam/history")
      ]);
      setProgress(progressRes.data);
      setExams(examsRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const totalQuestions = progress.reduce((sum, p) => sum + (p.questions_attempted || 0), 0);
  const totalCorrect = progress.reduce((sum, p) => sum + (p.questions_correct || 0), 0);
  const overallAccuracy = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;
  const avgExamScore = exams.length > 0 
    ? Math.round(exams.reduce((sum, e) => sum + e.score_percentage, 0) / exams.length)
    : 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="btn-ghost p-2">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-xl font-bold text-slate-900">Your Progress</h1>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Stats overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{totalQuestions}</p>
                <p className="text-sm text-slate-500">Questions Done</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{overallAccuracy}%</p>
                <p className="text-sm text-slate-500">Overall Accuracy</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{exams.length}</p>
                <p className="text-sm text-slate-500">Mock Exams</p>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{avgExamScore}%</p>
                <p className="text-sm text-slate-500">Avg Exam Score</p>
              </div>
            </div>
          </div>
        </div>

        {/* Progress by discipline */}
        {progress.length > 0 && (
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Progress by Discipline</h2>
            <div className="space-y-3">
              {progress.map((p, i) => {
                const accuracy = p.questions_attempted > 0 
                  ? Math.round((p.questions_correct / p.questions_attempted) * 100) 
                  : 0;
                return (
                  <div key={i} className="card">
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <p className="font-medium text-slate-900">
                          {p.exam_type} - {p.discipline?.replace(/_/g, " ")}
                        </p>
                        <p className="text-sm text-slate-500">
                          {p.questions_correct}/{p.questions_attempted} correct
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        accuracy >= 70 ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                      }`}>
                        {accuracy}%
                      </span>
                    </div>
                    <div className="progress-bar">
                      <div className="progress-bar-fill" style={{ width: `${accuracy}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Mock exam history */}
        {exams.length > 0 && (
          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Mock Exam History</h2>
            <div className="space-y-3">
              {exams.map((exam, i) => (
                <div key={i} className="card flex justify-between items-center">
                  <div>
                    <p className="font-medium text-slate-900">
                      {exam.exam_type} - {exam.discipline?.replace(/_/g, " ")}
                    </p>
                    <p className="text-sm text-slate-500">
                      {exam.correct_answers}/{exam.total_questions} correct • 
                      {Math.round(exam.time_taken_seconds / 60)} min
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    exam.score_percentage >= 70 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                  }`}>
                    {Math.round(exam.score_percentage)}%
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {progress.length === 0 && exams.length === 0 && (
          <div className="text-center py-12">
            <p className="text-slate-600 mb-4">No progress yet. Start practicing!</p>
            <button
              onClick={() => navigate("/exam-selection")}
              className="btn-primary"
            >
              Start Practicing
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
